import { Badge, PriceBlock, ProductCard } from '@/components/ui';
import { formatPrice } from '@/utils/format';
import type { Product } from '@/types';
import { ProductCarousel } from '../ProductCarousel';
import { recommendedProductsStyles } from './RecommendedProducts.styles';

export type RecommendedProductsProps = {
  products: Product[];
  title: string;
  emptyLabel?: string;
  inStockLabel: string;
  outOfStockLabel: string;
  stockLabel?: (product: Product) => string;
  onProductClick: (productId: string) => void;
};

const PLACEHOLDER = 'https://via.placeholder.com/300x300?text=No+Image';

export function RecommendedProducts({
  products,
  title,
  emptyLabel,
  inStockLabel,
  outOfStockLabel,
  stockLabel,
  onProductClick,
}: RecommendedProductsProps): JSX.Element {
  if (!products.length) {
    return (
      <section className={recommendedProductsStyles.root}>
        <h2 className="mb-3 font-heading text-heading-md text-brand-950 sm:text-heading-lg">
          {title}
        </h2>
        {emptyLabel ? (
          <p className={recommendedProductsStyles.empty}>{emptyLabel}</p>
        ) : null}
      </section>
    );
  }

  return (
    <section className={recommendedProductsStyles.root}>
      <ProductCarousel title={title}>
        {products.map((product) => {
          const isInStock = (product.stock ?? 0) > 0;
          const imageUrl =
            product.images && product.images.length > 0
              ? product.images[0].url
              : PLACEHOLDER;

          return (
            <ProductCard
              key={product.id}
              imageUrl={imageUrl}
              title={product.name}
              onClick={() => onProductClick(product.id)}
              price={
                <PriceBlock
                  amount={formatPrice(product.price, product.currency)}
                  size="sm"
                />
              }
              badge={
                <Badge variant={isInStock ? 'success' : 'danger'}>
                  {isInStock ? stockLabel?.(product) ?? inStockLabel : outOfStockLabel}
                </Badge>
              }
            />
          );
        })}
      </ProductCarousel>
    </section>
  );
}
